/**
 * FeinFutter – component-cart.js: Warenkorb-Drawer und /cart (Doku: sections/cart-drawer.liquid, main-cart.liquid).
 * <ff-cart data-section-id data-context="drawer|page" data-inner-id data-count-id>: Menge/Entfernen per FF.cart +
 * Section Rendering API (nur [data-cart-inner] wird ersetzt), Fehler, Staffel (window.ProfitKoala, nur lesend),
 * optionale Shopify-Empfehlungen. Global: ff:cart:added öffnet den Drawer (außer detail.openDrawer === false: nach dem
 * Schnellkauf mit quick_add_after = toast zeigt component-toast.js den Hinweis); ff:cart:refresh (auch von
 * App-Änderungen, erkannt in global.js), visibilitychange und pageshow gleichen ab.
 *
 * Laden: auf /cart per <script> aus main-cart; für den Drawer lädt global.js das Modul (und component-cart.css) erst
 * bei Bedarf (C-PERF-04) und spielt Events von vorher über FF.cartUI.replay(name, detail) nach.
 * window.FF.cartUI = { ready, refresh, open, check, replay }. Messung: docs/CONVERSION.md C-MEAS-02.
 */

const FF = (window.FF = window.FF || {});
const DRAWER_ID = 'CartDrawer';
const EXPRESS_TAGS = [
  'shop-pay-wallet-button',
  'shopify-paypal-button',
  'shopify-apple-pay-button',
  'shopify-google-pay-button',
  'shopify-amazon-pay-button',
  'shopify-venmo-button',
];

/** @type {Set<FFCart>} */
const instances = new Set();
let openSource = null;
let lastAction = null;
let lastCheck = 0;

/* ---------------------------------------------------------------------------
 * Hilfen
 * ------------------------------------------------------------------------- */

function publish(name, payload) {
  try {
    const analytics = window.Shopify && window.Shopify.analytics;
    if (analytics && typeof analytics.publish === 'function') analytics.publish(name, payload);
  } catch (error) {
    /* Messung darf den Kauf nie stören */
  }
}

function debounce(fn, wait) {
  if (FF.utils && FF.utils.debounce) return FF.utils.debounce(fn, wait);
  let timer;
  const run = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
  run.cancel = () => clearTimeout(timer);
  return run;
}

function announce(message, politeness) {
  if (message && FF.utils && FF.utils.announce) FF.utils.announce(message, politeness);
}

/** „Noch [n] Würste …“ → Platzhalter [name] ersetzen */
function fill(template, values) {
  return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
}

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

function drawerElement() {
  return document.getElementById(DRAWER_ID);
}

function drawerIsOpen() {
  const drawer = drawerElement();
  return Boolean(drawer && drawer.hasAttribute('open'));
}

/**
 * Staffelstufen aus Upsell Koala (Struktur wie assets/product-data.js → tiersFromKoala, defensiv gelesen).
 * Nur Prozent-Rabatte, sonst null – so erscheint nie ein Rabatt, den der Warenkorb nicht rechnet.
 * @returns {{ min: number, pct: number }[]|null}
 */
function koalaTiers(productId) {
  const registry = window.ProfitKoala;
  if (!registry || typeof registry !== 'object') return null;
  try {
    const entries = Object.values(registry).filter(
      (entry) =>
        entry &&
        Array.isArray(entry.offers) &&
        entry.offers.length &&
        String(entry.product && entry.product.id) === String(productId)
    );
    const entry = entries.find((item) => item.deal && item.deal.type === 'CART_VOLUME_DISCOUNTS') || entries[0];
    if (!entry) return null;
    const offers = entry.offers.filter((offer) => offer && Number.isFinite(Number(offer.minQuantity)));
    if (!offers.length || offers.some((offer) => offer.discount && offer.discount.type !== 'percentage')) return null;
    const tiers = offers
      .map((offer) => ({
        min: Math.max(1, Math.round(Number(offer.minQuantity))),
        pct: Number(offer.discount && offer.discount.value) || 0,
      }))
      .filter((tier) => tier.pct > 0 && tier.pct < 100)
      .sort((a, b) => a.min - b.min);
    return tiers.length ? tiers : null;
  } catch (error) {
    return null;
  }
}

/** Sections, die nach einer Änderung neu gerendert werden (Standard + alle <ff-cart> der Seite). */
function sectionIds() {
  const ids = new Set(FF.cart && FF.cart.getSectionsToRender ? FF.cart.getSectionsToRender() : []);
  instances.forEach((instance) => ids.add(instance.sectionId));
  if (!drawerElement()) ids.delete('cart-drawer');
  return [...ids];
}

function currentSignature() {
  const inner = document.querySelector('[data-cart-inner][data-signature]');
  return inner ? inner.getAttribute('data-signature') : null;
}

/* ---------------------------------------------------------------------------
 * Öffnen und Abgleich
 * ------------------------------------------------------------------------- */

/**
 * Öffnet den Drawer (nur Warenkorb-Art „drawer“).
 * @param {string} source add | app | icon | editor
 * @param {HTMLElement|null} [trigger]
 */
async function openDrawer(source, trigger = null) {
  if (!FF.settings || FF.settings.cartType !== 'drawer') return;
  // Styles des Drawers kommen mit dem Modul (global.js); vor dem Öffnen sicher geladen
  if (FF.utils && FF.utils.loadStyle) await FF.utils.loadStyle(FF.assets && FF.assets.cartCss);
  const drawer = drawerElement();
  if (!drawer || typeof drawer.open !== 'function' || drawer.hasAttribute('open')) return;
  openSource = source;
  drawer.open(trigger);
}

/**
 * Lädt Warenkorb und Sections neu und feuert ff:cart:updated.
 * @param {{ source?: string, open?: boolean }} [options]
 */
async function refreshCart({ source = 'refresh', open = false } = {}) {
  if (!FF.cart || !FF.sections) return;
  lastCheck = Date.now();
  try {
    const [cart, sections] = await Promise.all([FF.cart.get(), FF.sections.fetch(undefined, sectionIds())]);
    FF.events.emit('ff:cart:updated', { cart, sections, source, refreshed: true });
    if (open) openDrawer(source);
  } catch (error) {
    /* ff:cart:error meldet global.js; der alte Stand bleibt stehen */
  }
}

/** Gleicht nur ab, wenn sich Anzahl oder Summe geändert haben (Tab-Wechsel, Back/Forward-Cache). */
async function refreshIfChanged(source) {
  if (!instances.size || !FF.cart) return;
  lastCheck = Date.now();
  try {
    const cart = await FF.cart.get();
    if (currentSignature() === `${cart.item_count}-${cart.total_price}`) return;
    const sections = await FF.sections.fetch(undefined, sectionIds());
    FF.events.emit('ff:cart:updated', { cart, sections, source, refreshed: true });
  } catch (error) {
    /* still */
  }
}


/* ---------------------------------------------------------------------------
 * <ff-cart>
 * ------------------------------------------------------------------------- */

class FFCart extends HTMLElement {
  constructor() {
    super();
    this.pending = new Map();
    this.titles = new Map();
    this.lastGap = null;
    this.commitSoon = debounce(() => this.commit(), 350);
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onChangeCapture = this.onChangeCapture.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.context = this.dataset.context === 'page' ? 'page' : 'drawer';
    this.strings = Object.assign(
      {
        error: FF.strings && FF.strings.cartError,
        networkError: FF.strings && FF.strings.networkError,
        quantityError: FF.strings && FF.strings.quantity_error,
      },
      this.readStrings()
    );
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChangeCapture, true);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('submit', this.onSubmit);
    instances.add(this);

    if (this.context === 'page' && FF.cart && FF.cart.registerSections) {
      FF.cart.registerSections([this.sectionId]);
      if (!drawerElement() && Array.isArray(FF.cart.sectionsToRender)) {
        FF.cart.sectionsToRender = FF.cart.sectionsToRender.filter((id) => id !== 'cart-drawer');
      }
    }
    this.afterRender(true);
  }

  disconnectedCallback() {
    instances.delete(this);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChangeCapture, true);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('submit', this.onSubmit);
    this.commitSoon.cancel && this.commitSoon.cancel();
    clearTimeout(this.staffelTimer);
  }

  readStrings() {
    const node = this.querySelector('script[data-cart-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  /** @returns {HTMLElement|null} */
  get inner() {
    return (this.dataset.innerId && document.getElementById(this.dataset.innerId)) || this.querySelector('[data-cart-inner]');
  }

  /* ----------------------------- Rendern ----------------------------- */

  /**
   * Setzt den Inhalt aus gerendertem Section-HTML ein.
   * @param {Record<string, string|null>|null|undefined} sections
   * @returns {boolean} false, wenn die eigene Section fehlt
   */
  renderFrom(sections) {
    const html = sections && sections[this.sectionId];
    const inner = this.inner;
    if (!html || !inner) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = doc.getElementById(inner.id);
    if (!source) return false;

    const active = document.activeElement;
    const hadFocus = Boolean(active && inner.contains(active));
    const focusId = hadFocus && active.id ? active.id : '';
    const body = inner.querySelector('.cart-drawer__body');
    const scrollTop = body ? body.scrollTop : 0;

    ['data-item-count', 'data-total', 'data-signature'].forEach((name) => {
      inner.setAttribute(name, source.getAttribute(name) || '');
    });
    inner.replaceChildren(...source.childNodes);

    if (this.dataset.countId) {
      const target = document.getElementById(this.dataset.countId);
      const next = doc.getElementById(this.dataset.countId);
      if (target && next) target.innerHTML = next.innerHTML;
    }

    const newBody = inner.querySelector('.cart-drawer__body');
    if (newBody && scrollTop) newBody.scrollTop = scrollTop;

    if (hadFocus) {
      const target = (focusId && document.getElementById(focusId)) || this.focusFallback();
      if (target) target.focus({ preventScroll: true });
    }
    if (FF.reveal) FF.reveal.observe(inner);
    this.afterRender(false);
    return true;
  }

  focusFallback() {
    if (this.context === 'drawer') return document.getElementById(`${DRAWER_ID}-Title`);
    const section = this.closest('.section');
    return (
      (this.inner && this.inner.querySelector('.quantity__input')) ||
      (section && section.querySelector('.main-cart__title'))
    );
  }

  afterRender(initial) {
    const inner = this.inner;
    if (!inner) return;
    const bar = inner.querySelector('[data-free-shipping]');
    const gap = bar ? toNumber(bar.getAttribute('data-gap')) : null;
    if (!initial && gap === 0 && this.lastGap > 0) {
      publish('feinfutter:free_shipping_reached', {
        total_cents: toNumber(bar.getAttribute('data-total')),
        source: lastAction || 'unknown',
      });
    }
    this.lastGap = gap;
    this.enhanceStaffel(0);
    if (this.context === 'page' || drawerIsOpen()) this.loadRecommendations();
  }

  /* ----------------------------- Staffel ----------------------------- */

  enhanceStaffel(attempt) {
    clearTimeout(this.staffelTimer);
    const box = this.inner && this.inner.querySelector('[data-staffel]');
    if (!box) return;
    let tiers = null;
    for (const id of (box.dataset.productIds || '').split(',').filter(Boolean)) {
      tiers = koalaTiers(id);
      if (tiers) break;
    }
    if (!tiers) {
      // Koala schreibt window.ProfitKoala asynchron (App-Embed) – kurz erneut versuchen.
      if (attempt < 6) this.staffelTimer = setTimeout(() => this.enhanceStaffel(attempt + 1), 500);
      return;
    }
    const count = toNumber(box.dataset.count);
    const next = tiers.find((tier) => tier.min > count);
    const need = next ? next.min - count : 0;
    if (!next || need > (toNumber(box.dataset.distance) || 5)) {
      box.hidden = true;
      return;
    }
    const pct = String(next.pct).replace('.', ',');
    const text = box.querySelector('[data-staffel-text]');
    if (text) {
      text.textContent = fill(need === 1 ? box.dataset.textPercentOne : box.dataset.textPercentOther, { n: need, p: pct });
    }
    const button = box.querySelector('[data-staffel-add]');
    if (button) {
      const target = toNumber(box.dataset.lineQuantity) + need;
      const max = toNumber(box.dataset.lineMax);
      const item = box.dataset.item || '';
      button.hidden = max > 0 && target > max;
      button.dataset.quantity = String(target);
      button.dataset.tierMin = String(next.min);
      button.textContent = fill(box.dataset.addText, { n: need, item });
      button.setAttribute('aria-label', fill(box.dataset.addLabel, { n: need, item }));
    }
    box.hidden = false;
  }

  /* ----------------------------- Empfehlungen ----------------------------- */

  async loadRecommendations() {
    const box = this.inner && this.inner.querySelector('[data-cart-upsell][data-recommendations-url]');
    if (!box || box.dataset.loaded || this.recommendationsLoading) return;
    const url = box.dataset.recommendationsUrl;
    const key = `${url}|${currentSignature()}`;
    this.recommendationsLoading = true;
    try {
      let found = this.recommendationsCache && this.recommendationsCache.key === key ? this.recommendationsCache.html : null;
      if (found === null) {
        found = await this.fetchRecommendations(url);
        if (!found && url.includes('intent=complementary')) {
          found = await this.fetchRecommendations(url.replace('intent=complementary', 'intent=related'));
        }
        this.recommendationsCache = { key, html: found || '' };
      }
      const current = this.inner && this.inner.querySelector('[data-cart-upsell][data-recommendations-url]');
      if (!current) return;
      if (found) {
        const template = document.createElement('template');
        template.innerHTML = found;
        const next = template.content.firstElementChild;
        if (next) {
          next.dataset.loaded = 'true';
          next.hidden = false;
          current.replaceWith(next);
          return;
        }
      }
      current.dataset.loaded = 'true';
    } catch (error) {
      /* Kollektion bleibt stehen */
    } finally {
      this.recommendationsLoading = false;
    }
  }

  async fetchRecommendations(url) {
    const response = await window.fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return '';
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const box = doc.querySelector('[data-cart-upsell][data-source="recommendations"]');
    return box && box.querySelector('.upsell-card') ? box.outerHTML : '';
  }

  /* ----------------------------- Ereignisse ----------------------------- */

  onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest) return;

    const remove = target.closest('[data-cart-remove]');
    if (remove && this.contains(remove)) {
      event.preventDefault();
      this.clearError();
      lastAction = 'change';
      this.queue(remove.getAttribute('data-line-key'), 0, true);
      return;
    }

    const staffel = target.closest('[data-staffel-add]');
    if (staffel && this.contains(staffel)) {
      event.preventDefault();
      const quantity = toNumber(staffel.dataset.quantity);
      if (quantity < 1 || staffel.getAttribute('aria-busy') === 'true') return;
      this.clearError();
      staffel.setAttribute('aria-busy', 'true');
      lastAction = 'staffel';
      const box = staffel.closest('[data-staffel]');
      publish('feinfutter:staffel_tier_selected', {
        placement: (box && box.dataset.placement) || this.context,
        quantity,
        tier_min: toNumber(staffel.dataset.tierMin),
      });
      this.queue(staffel.dataset.lineKey, quantity, true);
      return;
    }

    const express = target.closest('[data-express-checkout]');
    if (express && this.contains(express)) {
      let method = 'unknown';
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      for (const node of path) {
        const tag = node && node.tagName ? node.tagName.toLowerCase() : '';
        if (EXPRESS_TAGS.includes(tag)) {
          method = tag.replace(/^shopify-/, '').replace(/-wallet-button$|-button$/, '');
          break;
        }
      }
      publish('feinfutter:express_checkout_click', { placement: this.context === 'drawer' ? 'drawer' : 'cart', method });
    }
  }

  /** Capture: Eingaben über dem Limit melden, bevor <ff-quantity> sie begrenzt. */
  onChangeCapture(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-quantity-input')) return;
    // Enter löst zwei change-Events aus (eigenes aus onKeydown + natives des Browsers). Das zweite trägt den schon
    // begrenzten Wert – ignorieren, sonst verschwindet die Limit-Meldung sofort wieder.
    if (input.dataset.processedValue !== undefined && input.dataset.processedValue === input.value) {
      event.stopPropagation();
      return;
    }
    this.clearError();
    const value = parseInt(input.value, 10);
    const max = parseInt(input.max, 10);
    if (Number.isFinite(value) && Number.isFinite(max) && max > 0 && value > max) {
      const limit = parseInt(input.getAttribute('data-limit') || '', 10);
      this.showError(max === limit ? fill(this.strings.limit, { max }) : this.strings.quantityError);
    }
  }

  onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLElement)) return;
    if (input.hasAttribute('data-quantity-input')) {
      // Wert nach der Begrenzung durch <ff-quantity> merken (siehe onChangeCapture)
      input.dataset.processedValue = /** @type {HTMLInputElement} */ (input).value;
      const quantity = parseInt(/** @type {HTMLInputElement} */ (input).value, 10);
      const key = input.getAttribute('data-line-key');
      if (!Number.isFinite(quantity) || quantity < 0 || !key) return;
      const line = input.closest('[data-cart-item]');
      if (line && toNumber(line.getAttribute('data-quantity')) === quantity && !this.pending.has(key)) return;
      lastAction = 'change';
      this.queue(key, quantity, false);
    } else if (input.hasAttribute('data-cart-note') && FF.cart) {
      FF.cart.update(null, { note: /** @type {HTMLTextAreaElement} */ (input).value, sections: false }).catch(() => {});
    }
  }

  onKeydown(event) {
    const input = event.target;
    if (event.key !== 'Enter' || !(input instanceof HTMLInputElement) || !input.hasAttribute('data-quantity-input')) return;
    // Enter im Mengenfeld: übernehmen statt das Formular (und damit „Zur Kasse“) abzuschicken.
    event.preventDefault();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-cart-form')) return;
    const submitter = event.submitter;
    if (submitter && submitter.getAttribute('name') === 'checkout') {
      submitter.setAttribute('aria-busy', 'true');
      return;
    }
    event.preventDefault();
    this.commitSoon.cancel && this.commitSoon.cancel();
    this.commit();
  }

  /* ----------------------------- Änderungen ----------------------------- */

  lineFor(key) {
    return this.inner ? this.inner.querySelector(`[data-cart-item][data-line-key="${CSS.escape(key)}"]`) : null;
  }

  queue(key, quantity, now) {
    if (!key) return;
    const line = this.lineFor(key);
    if (line) {
      line.setAttribute('aria-busy', 'true');
      this.titles.set(key, line.getAttribute('data-title') || '');
    }
    this.pending.set(key, Math.max(0, quantity));
    if (now) {
      this.commitSoon.cancel && this.commitSoon.cancel();
      this.commit();
    } else {
      this.commitSoon();
    }
  }

  async commit() {
    if (!this.pending.size || !FF.cart) return;
    const entries = [...this.pending.entries()];
    this.pending.clear();
    this.setBusy(true);
    try {
      const sections = sectionIds();
      const result =
        entries.length === 1
          ? await FF.cart.change(entries[0][0], entries[0][1], { sections })
          : await FF.cart.update(Object.fromEntries(entries), { sections });
      const items = (result && result.items) || [];
      const limited = entries.some(([key, quantity]) => {
        const item = items.find((entry) => entry.key === key);
        return quantity > 0 && item && item.quantity < quantity;
      });
      FF.events.emit('ff:cart:updated', {
        cart: result,
        sections: result && result.sections,
        source: this.context === 'drawer' ? 'cart-drawer' : 'main-cart',
      });
      if (limited) {
        this.showError(this.strings.quantityError);
      } else {
        const removed = entries.filter(([, quantity]) => quantity === 0);
        announce(
          removed.length === 1
            ? fill(this.strings.removed, { title: this.titles.get(removed[0][0]) || '' })
            : this.strings.updated
        );
      }
    } catch (error) {
      // Meldung kommt über ff:cart:error; Stand vom Server holen, damit die Mengen wieder stimmen.
      await refreshCart({ source: 'refresh' });
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    const inner = this.inner;
    if (!inner) return;
    inner.classList.toggle('is-busy', busy);
    if (busy) {
      inner.setAttribute('aria-busy', 'true');
    } else {
      inner.removeAttribute('aria-busy');
      inner.querySelectorAll('[data-cart-item][aria-busy], [data-staffel-add][aria-busy]').forEach((node) => {
        if (!this.pending.has(node.getAttribute('data-line-key'))) node.removeAttribute('aria-busy');
      });
    }
  }

  /* ----------------------------- Fehler ----------------------------- */

  errorBox() {
    return this.querySelector('[data-cart-error]');
  }

  showError(message) {
    const box = this.errorBox();
    if (!box || !message) return;
    box.textContent = message;
    announce(message, 'assertive');
  }

  clearError() {
    const box = this.errorBox();
    if (box) box.textContent = '';
  }

  /** ff:cart:error → nur anzeigen, wenn dieser Warenkorb sichtbar ist (Drawer offen bzw. /cart). */
  handleError(detail) {
    if (this.context === 'drawer' && !drawerIsOpen()) return;
    let message = this.strings.error;
    if (detail.status === 0) message = this.strings.networkError;
    else if (detail.status === 422) message = this.strings.quantityError;
    this.showError(message || detail.message);
  }
}

if (!customElements.get('ff-cart')) customElements.define('ff-cart', FFCart);

/* ---------------------------------------------------------------------------
 * Globale Listener (einmal je Seite)
 * ------------------------------------------------------------------------- */

function renderAll(sections) {
  let complete = true;
  instances.forEach((instance) => {
    if (!instance.renderFrom(sections)) complete = false;
  });
  return complete;
}

/** Handler der Warenkorb-Events (auch für FF.cartUI.replay: Events, die vor dem Laden des Moduls kamen). */
const handlers = {
  'ff:cart:added'(detail) {
    const form = detail.form instanceof HTMLFormElement ? detail.form : null;
    const upsell = Boolean(form && form.hasAttribute('data-upsell-form'));
    lastAction = upsell ? 'upsell' : 'add';
    if (!renderAll(detail.sections)) refreshCart({ source: 'add' });
    if (upsell) {
      publish('feinfutter:upsell_added', {
        placement: form.getAttribute('data-placement') || 'drawer',
        product_id: form.getAttribute('data-product-id') || '',
        variant_id: form.getAttribute('data-variant-id') || '',
        price_cents: toNumber(form.getAttribute('data-price')),
      });
    }
    // Schnellkauf (Karten-„+“, Schnellkauf-Dialog, source 'quick-add') mit Einstellung quick_add_after = toast:
    // global.js setzt vorher (Capture-Phase) detail.openDrawer = false und zeigt den Hinweis (C-COL-05).
    // Kaufbox, Kaufleiste, Upsell & Co. öffnen weiterhin den Drawer.
    if (detail.openDrawer === false) return;
    // Fokus nach dem Schließen: Auslöser, falls noch sichtbar (nicht z. B. im geschlossenen Schnellkauf-Dialog)
    const trigger =
      detail.trigger instanceof HTMLElement && detail.trigger.isConnected && detail.trigger.getClientRects().length
        ? detail.trigger
        : null;
    openDrawer('add', trigger);
  },

  'ff:cart:updated'(detail) {
    if (!renderAll(detail.sections) && !detail.refreshed) refreshCart({ source: 'refresh' });
  },

  'ff:cart:error'(detail) {
    instances.forEach((instance) => instance.handleError(detail));
  },

  'ff:drawer:open'(detail) {
    if (detail.id !== DRAWER_ID) return;
    const inner = document.getElementById(`${DRAWER_ID}-Inner`);
    const bar = inner && inner.querySelector('[data-free-shipping]');
    publish('feinfutter:cart_drawer_opened', {
      source: openSource || 'icon',
      item_count: inner ? toNumber(inner.getAttribute('data-item-count')) : 0,
      total_cents: inner ? toNumber(inner.getAttribute('data-total')) : 0,
      gap_cents: bar ? toNumber(bar.getAttribute('data-gap')) : 0,
    });
    openSource = null;
    instances.forEach((instance) => {
      if (instance.context === 'drawer') instance.loadRecommendations();
    });
  },

  'ff:cart:refresh'(detail) {
    lastAction = detail.source || 'app';
    const drawer = drawerElement();
    const allowOpen = Boolean(drawer) && drawer.getAttribute('data-open-on-app-add') !== 'false';
    refreshCart({ source: detail.source || 'app', open: Boolean(detail.open) && allowOpen });
  },
};

Object.keys(handlers).forEach((name) => {
  document.addEventListener(name, (event) => handlers[name](/** @type {CustomEvent} */ (event).detail || {}));
});

FF.cartUI = {
  ready: true,
  refresh: refreshCart,
  open: openDrawer,
  /** Abgleich nur bei Änderung (z. B. nach dem Back/Forward-Cache), @param {string} source */
  check: refreshIfChanged,
  /** @param {string} name Event-Name @param {object} [detail] */
  replay(name, detail) {
    if (handlers[name]) handlers[name](detail || {});
  },
};

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && Date.now() - lastCheck > 10000) refreshIfChanged('visibility');
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('ff-cart [aria-busy="true"]').forEach((node) => node.removeAttribute('aria-busy'));
  instances.forEach((instance) => instance.setBusy(false));
  refreshIfChanged('pageshow');
});

// Theme-Editor: Drawer beim Auswählen der Section öffnen und nach dem Neuladen offen halten.
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:select', (event) => {
    if (event.detail && event.detail.sectionId === 'cart-drawer' && FF.drawers) FF.drawers.open(DRAWER_ID);
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail && event.detail.sectionId === 'cart-drawer' && FF.drawers) FF.drawers.open(DRAWER_ID);
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.detail && event.detail.sectionId === 'cart-drawer' && FF.drawers) FF.drawers.close(DRAWER_ID);
  });
}

